
import React, { useState, useMemo, useEffect } from 'react';
import { useComments } from '@/hooks/comments';
import CommentWithReplies from './comment/CommentWithReplies';
import CommentForm from './comment/CommentForm';
import { Button } from '@/components/ui/button';
import { ArrowUpDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CommentHeader from './comment/CommentHeader';
import { useCommentCount } from '@/contexts/comments/CommentCountContext';
import { SortOption } from './comment/CommentSortControls';
import { CommentSortOption } from '@/services/comments/types';
import { useAuth } from '@/contexts/auth';

export interface ThreadedCommentsProps {
  contentId: string;
  contentType?: 'poll' | 'card';
  initialPageSize?: number;
}

const ThreadedComments: React.FC<ThreadedCommentsProps> = ({
  contentId,
  contentType = 'poll',
  initialPageSize = 10
}) => {
  const { user, isAuthenticated } = useAuth();
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [pageSize, setPageSize] = useState(initialPageSize);
  
  // Map UI sort option to backend sort option
  const mapSortOption = (uiSortOption: SortOption): CommentSortOption => {
    if (uiSortOption === 'votes') return 'top';
    return uiSortOption as CommentSortOption;
  };
  
  // Use the CommentCount context for realtime updates
  const commentCountContext = useCommentCount();
  
  // Use the useComments hook with the correct sort option mapping
  const {
    comments,
    commentCount: hookCommentCount,
    isLoading,
    isSubmitting,
    handleSubmitComment,
    userVotes,
    voteCounts,
    isVoting,
    handleVote,
    maxVotes,
    // Thread features
    replies,
    repliesVisible,
    repliesLoading,
    toggleReplies,
    replyForm,
    toggleReplyForm,
    replyCounts,
    upvoteCounts,
    downvoteCounts
  } = useComments({
    contentId,
    limit: pageSize,
    contentType,
    sortBy: mapSortOption(sortBy),
    parentId: null
  });
  
  // Force update the comment count context when hook count changes
  useEffect(() => {
    if (commentCountContext?.commentCount === undefined && hookCommentCount > 0) {
      commentCountContext?.setCommentCount(hookCommentCount);
    }
  }, [hookCommentCount, commentCountContext]);
  
  // Use the context value if available, otherwise fall back to the hook value
  const displayCommentCount = commentCountContext?.commentCount ?? hookCommentCount;
  
  // Generate stable ID for comments
  const getCommentKey = useMemo(() => {
    // Use an object to store the stable indexes for comments
    const keyMap = new Map<string, string>();
    
    // Generate a stable ID for each comment
    comments.forEach((comment, index) => {
      if (!keyMap.has(comment.id)) {
        keyMap.set(comment.id, `comment-${index}-${comment.id}`);
      }
    });
    
    // Return a function that gets the key for a comment
    return (commentId: string) => keyMap.get(commentId) || commentId;
  }, [comments]);

  // Debug logging
  console.log(`ThreadedComments: Current comment count from context:`, commentCountContext?.commentCount);
  console.log(`ThreadedComments: Current comment count from hook:`, hookCommentCount);
  console.log(`ThreadedComments: Display comment count:`, displayCommentCount);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <CommentHeader commentCount={displayCommentCount} />
        
        {/* Sort controls */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
          <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="votes">Most votes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Comment form */}
      <CommentForm 
        isSubmitting={isSubmitting}
        onSubmit={handleSubmitComment}
        isAuthenticated={isAuthenticated}
      />

      {isLoading ? (
        <p className="text-center text-muted-foreground">Loading comments...</p>
      ) : comments.length > 0 ? (
        <div className="space-y-6">
          {comments.map((comment) => {
            // Use stable keys
            const commentKey = getCommentKey(comment.id);
            
            return (
              <CommentWithReplies
                key={commentKey}
                comment={comment}
                userVotes={userVotes}
                upvoteCounts={upvoteCounts}
                downvoteCounts={downvoteCounts}
                maxVotes={maxVotes}
                isVoting={isVoting}
                handleVote={handleVote}
                replyForm={replyForm}
                toggleReplyForm={toggleReplyForm}
                replies={replies}
                repliesVisible={repliesVisible}
                repliesLoading={repliesLoading}
                toggleReplies={toggleReplies}
                replyCounts={replyCounts}
                isAuthenticated={isAuthenticated}
                isSubmitting={isSubmitting}
                handleSubmitComment={handleSubmitComment}
              />
            );
          })}
        </div>
      ) : (
        <p className="text-center text-muted-foreground">No comments yet. Be the first to comment!</p>
      )}
      
      {displayCommentCount > comments.length && (
        <div className="text-center mt-4">
          <Button 
            variant="outline" 
            onClick={() => setPageSize(prev => prev + 10)}
            className="flex items-center gap-2"
          >
            Load More Comments ({comments.length} of {displayCommentCount})
          </Button>
        </div>
      )}
    </div>
  );
};

export default ThreadedComments;
